import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import {
  deleteCodeDirectory,
  updateCodeDirectoryTags,
} from '@/features/codeCoverage/data/codeDirectories';
import { emitCoverageRefresh } from '@/features/codeCoverage/state/coverageRefreshSignal';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import { queryKeys } from '@/shared/hooks';
type UseCodeDirectoryTableRowActionsParams = {
  selectedProjectId: string | null;
};

type DeleteTarget = {
  id: string;
  name: string;
};

type UseCodeDirectoryTableRowActionsResult = {
  deleteLoading: boolean;
  deleteTarget: DeleteTarget | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onDeleteRow: (row: { id: string; name?: string | null }) => void;
  onUpdateRowTagsColor: (input: {
    row: { id: string };
    tags: string[];
    colors: TagColor[];
  }) => void;
  updateRowTagsColorPendingId: string | null;
};

export const useCodeDirectoryTableRowActions = ({
  selectedProjectId,
}: UseCodeDirectoryTableRowActionsParams): UseCodeDirectoryTableRowActionsResult => {
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);

  const deleteCodeDirectoryMutation = useMutation({
    mutationFn: async (input: { id: string }) => {
      await deleteCodeDirectory(input, selectedOrganisationId);
      return input;
    },
    onSuccess: async (_deleted, input) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.codeDirectories(selectedOrganisationId ?? null, selectedProjectId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.codeDirectoryDetail(input.id, selectedOrganisationId ?? null),
        }),
      ]);
      emitCoverageRefresh();
      notify({
        message: getToastMessage('entity.deleted', {
          entity: 'code directory',
          name: deleteTarget?.name ?? input.id,
        }),
        tone: 'green',
      });
      setDeleteTarget(null);
    },
    onError: (error) => {
      notify({ message: toErrorMessage(error), tone: 'red' });
    },
  });

  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) return;
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(item, selectedOrganisationId ?? undefined);
        }),
      );
    },
  });

  const onUpdateRowTagsColor = useCallback(
    ({ row, tags, colors }: { row: { id: string }; tags: string[]; colors: TagColor[] }) => {
      const normalizedTags = Array.from(
        new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
      );
      const normalizedColors = colors.slice(0, normalizedTags.length);
      setUpdatingTagRowId(row.id);
      void (async () => {
        try {
          await updateCodeDirectoryTags(
            { id: row.id, tags: normalizedTags },
            selectedOrganisationId,
          );
          if (normalizedTags.length > 0) {
            await updateTagColorMutation.mutateAsync({
              tags: normalizedTags,
              colors: normalizedColors,
            });
          }
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.codeDirectories(
                selectedOrganisationId ?? null,
                selectedProjectId,
              ),
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.codeDirectoryDetail(row.id, selectedOrganisationId ?? null),
            }),
          ]);
          await queryClient.refetchQueries({ type: 'active' });
          notify({ message: getToastMessage('tags.updated'), tone: 'green' });
        } catch (error) {
          notify({ message: toErrorMessage(error), tone: 'red' });
        } finally {
          setUpdatingTagRowId(null);
        }
      })();
    },
    [
      notify,
      queryClient,
      selectedOrganisationId,
      selectedProjectId,
      toErrorMessage,
      updateTagColorMutation,
    ],
  );

  const onDeleteRow = useCallback((row: { id: string; name?: string | null }) => {
    setDeleteTarget({
      id: row.id,
      name: row.name?.trim() ?? row.id,
    });
  }, []);

  const onDeleteClose = useCallback(() => {
    if (deleteCodeDirectoryMutation.isPending) {
      return;
    }
    setDeleteTarget(null);
  }, [deleteCodeDirectoryMutation.isPending]);

  const onDeleteConfirm = useCallback(() => {
    if (!deleteTarget) {
      return;
    }
    deleteCodeDirectoryMutation.mutate({ id: deleteTarget.id });
  }, [deleteCodeDirectoryMutation, deleteTarget]);

  return {
    deleteLoading: deleteCodeDirectoryMutation.isPending,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId: updatingTagRowId,
  };
};
